import { Component, Input } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { QuestionType } from '@models/event-attendance/event-attendee-question-type.enum';
import { EventAttendeeQuestion } from '@models/event-attendance/event-attendee-question.model';
import { AccountRequirement } from '@models/events/account-requirement.enum';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventSlot } from '@models/events/event-slot.model';
import { BookingEventSlotAssignTicketCardComponent } from '@modules/events/components/booking-event-slot-assign-ticket-card/booking-event-slot-assign-ticket-card.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-booking-event-slot-assign-tickets',
  standalone: true,
  imports: [BookingEventSlotAssignTicketCardComponent, TranslateModule],
  templateUrl: './booking-event-slot-assign-tickets.component.html',
  styleUrl: './booking-event-slot-assign-tickets.component.scss',
})
export class BookingEventSlotAssignTicketsComponent {
  private _ticketOrder?: EventAttendeeTicketOrder;
  ticketForms = new Map<number, FormGroup>();
  private _dataFields?: EventAttendeeQuestion[];

  @Input() slot?: EventSlot;
  @Input() attendeeForm?: FormGroup;
  @Input()
  set dataFields(dataFields: EventAttendeeQuestion[] | undefined) {
    this._dataFields = dataFields;

    if (this._dataFields) {
      this.tryInitForm();
    }
  }
  getTicketFormArray(): FormGroup[] {
    return Array.from(this.ticketForms.values());
  }

  get dataFields(): EventAttendeeQuestion[] | undefined {
    return this._dataFields;
  }

  @Input()
  set ticketOrder(value: EventAttendeeTicketOrder | undefined) {
    this._ticketOrder = value;
    this.tryInitForm();
  }

  get ticketOrder(): EventAttendeeTicketOrder | undefined {
    return this._ticketOrder;
  }

  get areAllFormsValidOrTouchAll(): boolean {
    let allValid = true;

    this.ticketForms.forEach((formGroup) => {
      if (formGroup.invalid) {
        allValid = false;
        // formGroup.markAllAsTouched();
      }
    });

    return allValid;
  }

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
  ) {}

  private addQuestionControlsToAllForms(): void {
    if (!this._dataFields) return;

    this.ticketForms.forEach((formGroup) => {
      for (const dataField of this._dataFields!) {
        const controlName = `question#${dataField.id}`;
        if (!formGroup.contains(controlName)) {
          formGroup.addControl(
            controlName,
            this.formBuilder.control(
              '',
              dataField.isRequired && dataField.type !== QuestionType.CHECKBOX
                ? Validators.required
                : [],
            ),
          );
        }
      }
    });
  }

  private tryInitForm(): void {
    this.initTicketForms();
    this.addQuestionControlsToAllForms();
  }

  initTicketForms() {
    this.ticketOrder?.eventAttendeeTickets?.forEach((ticket, index) => {
      const formGroup = this.formBuilder.group(
        {
          fillInDataLater: [false],
          firstName: [''],
          lastName: [''],
          email: ['', Validators.email],
          assignToBuyer: [false],
        },
        {
          validators: [this.conditionalRequiredValidator()],
        },
      );

      formGroup.get('fillInDataLater')?.valueChanges.subscribe(() => {
        formGroup.get('email')?.updateValueAndValidity();
        formGroup.updateValueAndValidity();
      });

      this.ticketForms.set(index, formGroup);
    });
  }

  getDescription() {
    if (!this.slot) return;

    switch (this.slot.accountRequirement) {
      case AccountRequirement.REQUIRED:
        return this.translateService.instant(
          'APP.BOOK_EVENT_SLOT.ACCOUNT_REQUIRED_MESSAGE',
        );
      default:
        return '';
    }
  }

  conditionalRequiredValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const formGroup = group as FormGroup;

      const fillInDataLater = formGroup.get('fillInDataLater')?.value;
      const firstNameControl = formGroup.get('firstName');
      const lastNameControl = formGroup.get('lastName');
      const emailControl = formGroup.get('email');

      const isNoAccount =
        this.slot?.accountRequirement === AccountRequirement.NO_ACCOUNT;
      const isAccountRequired =
        this.slot?.accountRequirement === AccountRequirement.REQUIRED;

      const errors: ValidationErrors = {};

      if (fillInDataLater) {
        [firstNameControl, lastNameControl, emailControl].forEach(
          (c) => c?.setErrors(null),
        );
      } else {
        if (!emailControl?.value?.trim()) {
          emailControl?.setErrors({ emailRequired: true });
          errors['emailRequired'] = true;
        } else {
          emailControl?.setErrors(null);
        }

        if (isNoAccount) {
          if (!firstNameControl?.value?.trim()) {
            firstNameControl?.setErrors({ firstNameRequired: true });
            errors['firstNameRequired'] = true;
          } else {
            firstNameControl?.setErrors(null);
          }

          if (!lastNameControl?.value?.trim()) {
            lastNameControl?.setErrors({ lastNameRequired: true });
            errors['lastNameRequired'] = true;
          } else {
            lastNameControl?.setErrors(null);
          }
        } else {
          firstNameControl?.setErrors(null);
          lastNameControl?.setErrors(null);
        }
      }

      Object.keys(formGroup.controls).forEach((key) => {
        if (!key.startsWith('question#')) return;

        const control = formGroup.get(key);
        const questionId = +key.split('#')[1];
        const matchingQuestion = this.slot?.dataFields?.find(
          (q) => q.dataFieldId === questionId,
        );
        const isRequired = matchingQuestion?.isRequired;

        if (fillInDataLater || isAccountRequired || !isRequired) {
          control?.setErrors(null);
          return;
        }

        const value = control?.value;
        const isEmpty =
          value === null ||
          value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0) ||
          value === false;

        if (isRequired && isEmpty) {
          control?.setErrors({ required: true });
          errors[key] = true;
        } else {
          control?.setErrors(null);
        }
      });

      return Object.keys(errors).length ? errors : null;
    };
  }
}
